/**
 * board-concurrency — the pure, DOM-free logic that keeps the Run Board readable
 * and CHEAP when a lane floods (Story G). Concurrency is maximized on this host,
 * so a single run's lane can carry thousands of trials; rendering every card does
 * not scale. Every rule here is a pure function — client-side filter/search, the
 * by-arm regrouping, the collapse-to-summary aggregation, and the virtual-window
 * math — so the React layer only renders what these return and each rule is
 * unit-testable in isolation, exactly like `board-lanes` and `board-dwell`.
 *
 * Two Principle-1 invariants this module exists to hold:
 *   - Filter and search run ENTIRELY client-side over the already-loaded page —
 *     they issue ZERO new network calls.
 *   - A flooded lane never renders thousands of DOM nodes: above a threshold it
 *     collapses to one summary band, and when expanded it virtualizes to a small
 *     window. DOM node count stays bounded regardless of trial count.
 */

import { type DwellStats, dwellStats } from './board-dwell'
import { type LaneId, laneForStatus } from './board-lanes'
import type { TrialLite, TrialStatus } from './board-types'

/**
 * Default collapse threshold: a lane whose LOADED card count exceeds this folds
 * to a summary band (counts + dwell med/max) until expanded. Fifty is high enough
 * that an ordinary lane stays open and low enough that a flooded one never paints
 * hundreds of rows before the operator asks for them. Override per board.
 */
export const DEFAULT_COLLAPSE_THRESHOLD = 50

/** The grouping mode for the board — flat (snapshot order) or grouped by arm. */
export type GroupMode = 'flat' | 'arm'

// --- Client-side filter + search -------------------------------------------

/**
 * The active client-side filter over the loaded set. `activeLanes` null means
 * "all lanes shown"; an explicit set hides every lane not in it. `query` is a
 * free-text needle matched case-insensitively against trial id, scenario, arm,
 * and the `t#<index>` token. Both are applied with NO network call — the loaded
 * page is filtered in place (Principle 1).
 */
export interface BoardFilter {
  activeLanes: ReadonlySet<LaneId> | null
  query: string
}

/** The identity filter — everything shown, no search. */
export const NO_FILTER: BoardFilter = { activeLanes: null, query: '' }

/**
 * Pure toggle for the active-lane set when a status chip is clicked.
 *
 * - null (all shown) + click lane A → show all lanes EXCEPT A
 * - set S + click lane A (present) → S without A (or null if S\{A} == all lanes)
 * - set S + click lane A (absent) → S union {A} (or null if that == all lanes)
 */
export function nextActiveLanes(
  prev: ReadonlySet<LaneId> | null,
  laneId: LaneId,
  allLaneIds: readonly LaneId[]
): ReadonlySet<LaneId> | null {
  if (prev === null) {
    return new Set(allLaneIds.filter((id) => id !== laneId))
  }
  const next = new Set(prev)
  if (next.has(laneId)) {
    next.delete(laneId)
  } else {
    next.add(laneId)
  }
  return next.size === 0 || next.size === allLaneIds.length ? null : next
}

/** True when a lane is shown under the current filter (null set ⇒ all shown). */
export function isLaneActive(filter: BoardFilter, laneId: LaneId): boolean {
  return filter.activeLanes === null || filter.activeLanes.has(laneId)
}

/**
 * True when a trial matches the free-text query. An empty/whitespace query
 * matches everything. The needle is matched against the trial id, scenario, arm
 * id, and both the bare index and the `t#<index>` token an operator would type.
 */
export function matchesQuery(trial: TrialLite, query: string): boolean {
  const needle = query.trim().toLowerCase()
  if (needle === '') {
    return true
  }
  const haystack = [trial.id, trial.scenario, trial.armId, `t#${trial.trialIndex}`, String(trial.trialIndex)]
  return haystack.some((field) => field.toLowerCase().includes(needle))
}

/**
 * Apply the filter to the loaded trials, preserving snapshot order. Lane chips
 * and the search box compose: a trial survives only if its lane is active AND it
 * matches the query. Pure — returns a new array, never mutates the input.
 */
export function filterTrials(trials: readonly TrialLite[], filter: BoardFilter): TrialLite[] {
  return trials.filter(
    (trial) => isLaneActive(filter, laneForStatus(trial.status)) && matchesQuery(trial, filter.query)
  )
}

// --- By-arm grouping --------------------------------------------------------

/** One arm's trials within a lane, used by the by-arm grouping toggle. */
export interface ArmGroup {
  armId: string
  trials: TrialLite[]
}

/**
 * Group a lane's trials by arm, in first-seen order. Because the snapshot is
 * ordered `arm_id, scenario, trial_index` (the read model's `listJobsByRun`
 * ordering), first-seen order is stable arm order — so a starved arm (few or no
 * trials advancing) is visible as a short or absent group. Flat mode skips this
 * entirely and renders the lane in snapshot order.
 */
export function groupByArm(trials: readonly TrialLite[]): ArmGroup[] {
  const order: string[] = []
  const byArm = new Map<string, TrialLite[]>()
  for (const trial of trials) {
    let bucket = byArm.get(trial.armId)
    if (bucket === undefined) {
      bucket = []
      byArm.set(trial.armId, bucket)
      order.push(trial.armId)
    }
    bucket.push(trial)
  }
  return order.map((armId) => ({ armId, trials: byArm.get(armId) as TrialLite[] }))
}

// --- Collapse-to-summary band ----------------------------------------------

/**
 * The dwell span (ms) that describes a trial's position in its current lane, for
 * the collapse summary. Each lane reads the timestamp that matters for it:
 *   - queued     → wait time since `createdAt`
 *   - leased     → time held since `leasedAt`
 *   - running    → retained sub-phase dwell if present, else stage dwell since `startedAt`
 *   - recovering → time since `leasedAt` (the reconcile window)
 *   - done       → wall duration `startedAt → completedAt`
 * Returns null when the governing timestamp is absent, so the aggregate simply
 * skips a trial it cannot place rather than inventing a zero.
 */
export function laneDwellMs(trial: TrialLite, now: number): number | null {
  switch (laneForStatus(trial.status)) {
    case 'queued':
      return Math.max(0, now - trial.createdAt)
    case 'leased':
      return trial.leasedAt === null ? null : Math.max(0, now - trial.leasedAt)
    case 'running':
      if (trial.phaseDwellMs !== null) {
        return Math.max(0, trial.phaseDwellMs)
      }
      return trial.startedAt === null ? null : Math.max(0, now - trial.startedAt)
    case 'recovering':
      return trial.leasedAt === null ? null : Math.max(0, now - trial.leasedAt)
    case 'done':
      if (trial.startedAt === null || trial.completedAt === null) {
        return null
      }
      return Math.max(0, trial.completedAt - trial.startedAt)
  }
}

/** The summary a collapsed lane shows in place of its cards. */
export interface LaneSummary {
  /** Loaded trials folded into this summary. */
  loaded: number
  /** Loaded count broken down by store status, omitting zero buckets. */
  byStatus: Partial<Record<TrialStatus, number>>
  /** Loaded Running trials broken down by retained sub-phase, omitting zeros. */
  bySubPhase: Record<string, number>
  /** Median + max lane dwell across the loaded trials (Story F's aggregate shape). */
  dwell: DwellStats
}

/**
 * Fold a lane's loaded trials into a summary band: counts by status, counts by
 * sub-phase (Running only), and the dwell med/max via Story F's aggregate. This
 * is what a flooded lane shows instead of N rows — `120 queued · wait med 4m`
 * rather than 120 cards (design spec, "High-concurrency behavior").
 */
export function summarizeLane(trials: readonly TrialLite[], now: number): LaneSummary {
  const byStatus: Partial<Record<TrialStatus, number>> = {}
  const bySubPhase: Record<string, number> = {}
  const dwellValues: number[] = []
  for (const trial of trials) {
    byStatus[trial.status] = (byStatus[trial.status] ?? 0) + 1
    if (trial.status === 'running' && trial.latestPhase !== null) {
      bySubPhase[trial.latestPhase] = (bySubPhase[trial.latestPhase] ?? 0) + 1
    }
    const dwell = laneDwellMs(trial, now)
    if (dwell !== null) {
      dwellValues.push(dwell)
    }
  }
  return { loaded: trials.length, byStatus, bySubPhase, dwell: dwellStats(dwellValues) }
}

/**
 * Whether a lane collapses by default: it does when its LOADED card count
 * exceeds the threshold. The comparison is against the loaded count — the cards
 * that would actually flood the DOM — not the authoritative store count, so a
 * lane whose store count is huge but whose page loaded few cards stays open.
 */
export function laneCollapsesByDefault(loadedCount: number, threshold: number = DEFAULT_COLLAPSE_THRESHOLD): boolean {
  return loadedCount > threshold
}

// --- Lane row model (unifies flat + by-arm for one virtual list) -------------

/**
 * One renderable row in an expanded lane body. A lane in flat mode is a list of
 * `card` rows; in by-arm mode it is arm-header rows interleaved with their
 * cards. Folding both modes into one flat row list means a single virtual window
 * bounds the DOM in EITHER mode — an arm-grouped flooded lane is as cheap as a
 * flat one.
 */
export type LaneRow = { kind: 'arm-header'; armId: string; count: number } | { kind: 'card'; trial: TrialLite }

/**
 * Build the flat row list for an expanded lane. In flat mode every row is a
 * card, in snapshot order. In by-arm mode each arm contributes a header row
 * followed by its card rows, in first-seen (= arm) order, so a starved arm reads
 * as a short or absent section. The result feeds {@link computeVirtualWindow}
 * directly via its length.
 */
export function buildLaneRows(trials: readonly TrialLite[], mode: GroupMode): LaneRow[] {
  if (mode === 'flat') {
    return trials.map((trial) => ({ kind: 'card', trial }))
  }
  const rows: LaneRow[] = []
  for (const group of groupByArm(trials)) {
    rows.push({ kind: 'arm-header', armId: group.armId, count: group.trials.length })
    for (const trial of group.trials) {
      rows.push({ kind: 'card', trial })
    }
  }
  return rows
}

// --- Virtual window ---------------------------------------------------------

/** Default rendered row height (px) used to map scroll offset → card index. */
export const DEFAULT_ROW_HEIGHT_PX = 28

/** Default scroll-viewport height (px) for a virtualized lane body. */
export const DEFAULT_VIEWPORT_PX = 480

/** Default overscan: rows rendered above/below the viewport to mask fast scroll. */
export const DEFAULT_OVERSCAN = 6

/** Inputs to the virtual-window computation for one scrollable lane body. */
export interface VirtualWindowParams {
  /** Total cards in the (filtered) lane. */
  total: number
  /** Pixel height of one rendered card row. */
  rowHeightPx?: number
  /** Pixel height of the scroll viewport. */
  viewportPx?: number
  /** Current scroll offset (px) of the lane body. */
  scrollTopPx: number
  /** Rows of overscan above and below the viewport. */
  overscan?: number
}

/**
 * The slice of cards to render for a virtualized lane, plus the spacer pads that
 * keep the scrollbar honest. Only `[startIndex, endIndex)` is in the DOM; the
 * pads reserve the height of the off-screen rows so a 5,000-card lane scrolls
 * exactly like a fully-rendered one while costing a constant ~window of nodes.
 */
export interface VirtualWindow {
  startIndex: number
  endIndex: number
  topPadPx: number
  bottomPadPx: number
}

/**
 * Compute the visible window for a virtualized lane body. The rendered node
 * count is bounded by `ceil(viewport / rowHeight) + 2·overscan` regardless of
 * `total`, which is the whole point — a flooded Done column costs the same as a
 * small one (Story G acceptance). Scrolling shifts the window without growing
 * it. Defensive against an over-scroll (clamps to the end) and a zero total.
 */
export function computeVirtualWindow(params: VirtualWindowParams): VirtualWindow {
  const rowHeightPx = params.rowHeightPx ?? DEFAULT_ROW_HEIGHT_PX
  const viewportPx = params.viewportPx ?? DEFAULT_VIEWPORT_PX
  const overscan = params.overscan ?? DEFAULT_OVERSCAN
  const total = Math.max(0, params.total)
  if (total === 0) {
    return { startIndex: 0, endIndex: 0, topPadPx: 0, bottomPadPx: 0 }
  }
  const visibleRows = Math.ceil(viewportPx / rowHeightPx) + 2 * overscan
  const scrollTop = Math.max(0, params.scrollTopPx)
  const firstVisible = Math.floor(scrollTop / rowHeightPx)
  const startIndex = Math.max(0, Math.min(firstVisible - overscan, Math.max(0, total - visibleRows)))
  const endIndex = Math.min(total, startIndex + visibleRows)
  return {
    startIndex,
    endIndex,
    topPadPx: startIndex * rowHeightPx,
    bottomPadPx: (total - endIndex) * rowHeightPx,
  }
}
